import { makeAutoObservable, runInAction } from "mobx";
import type RootStore from "stores/RootStore";
import type {
  GLTransaction,
  FiscalPeriod,
  AccountType,
  DateRangeType,
  GLSearchField,
  GLViewMode,
  TransactionSourceType,
  EnhancedGLAccount,
} from "../types";
import { PLANTS_BY_COMPANY } from "../types";
// ==================== Saved Report Configuration Type (Phase 7) ====================

export interface GLReportConfig {
  id: string;
  name: string;
  description?: string;
  createdBy: number;
  createdAt: string;
  isShared: boolean;

  // Filter Configuration
  filters: {
    dateRangeType: DateRangeType;
    customStartDate?: string;
    customEndDate?: string;
    plantIds: string[];
    accountIds: string[];
    accountTypes: AccountType[];
    vendorIds?: string[];
    customerIds?: string[];
  };

  // Display Configuration
  display: {
    visibleColumns: string[];
    columnOrder: string[];
    sortColumn: string;
    sortDirection: "asc" | "desc";
    tableDensity: "condensed" | "regular" | "relaxed";
  };
}
import {
  exportToCsv,
  exportToExcel,
  exportToExcelMultiSheet,
  exportToPdf,
  exportToPdfMultiSection,
  type ColumnConfig,
  type MultiSheetData,
  type PdfSectionData,
} from "shared/common/utils";

interface ExportMethodOptions {
  includeSummary?: boolean;
  includeHeaders?: boolean;
  splitByPlant?: boolean;
}

/**
 * General Ledger ViewStore
 * Implements drill-down hierarchy: Trial Balance → Account → Transactions
 * KEY FEATURES:
 * - Plant-based filtering
 * - Real-time balance calculation
 * - Keyboard-first navigation
 */
class GeneralLedgerStore {
  rootStore: RootStore;

  // ==================== View State ====================

  viewMode: GLViewMode = "transactions";
  selectedAccountId: string | null = null;
  selectedTransactionId: string | null = null;
  expandedRows: Set<string> = new Set();

  // ==================== Filtering ====================

  // Date filtering (with accounting-specific presets)
  dateRangeType: DateRangeType = "this_period";
  customStartDate: string | null = null;
  customEndDate: string | null = null;
  selectedPeriodId: string | null = null;

  // Dimension filtering
  selectedPlantIds: string[] = [];
  selectedJobId: string | null = null;
  selectedPhaseCode: string | null = null;
  selectedCostType: string | null = null;

  // Account filtering
  selectedAccountTypes: AccountType[] = [];
  selectedAccountIds: string[] = [];
  showZeroBalances = false;
  showInactiveAccounts = false;

  // Amount filtering
  amountFilterType: "any" | "debit" | "credit" = "any";
  amountMinimum: number | null = null;
  amountMaximum: number | null = null;
  materialityThreshold: number | null = null;

  // Search
  searchQuery = "";
  searchFields: GLSearchField[] = ["description", "reference", "account"];

  // ==================== Display Options ====================

  // Table density (per GL UI best practices)
  tableDensity: "condensed" | "regular" | "relaxed" = "regular";

  // Column visibility - Default per Brandi's requirements: simple view with drill-down
  visibleColumns: string[] = [
    "accountNumber",
    "accountName",
    "description",
    "debitAmount",
    "creditAmount",
  ];
  frozenColumns: string[] = ["accountNumber"];

  // Column order for drag-and-drop reordering
  columnOrder: string[] = [
    "accountNumber",
    "accountName",
    "description",
    "debitAmount",
    "creditAmount",
    "transactionDate",
    "plantId",
    "reference",
    "jobId",
    "runningBalance",
    "sourceType",
    "createdBy",
  ];

  // Pagination
  pageSize = 50;
  currentPage = 1;

  // Sorting
  sortColumn = "transactionDate";
  sortDirection: "asc" | "desc" = "desc";

  // ==================== UI State ====================

  isLoading = false;
  isExporting = false;
  selectedTransactionIds: Set<string> = new Set();
  lastKeyboardFocusedRow: number | null = null;

  // ==================== DataGrid Configuration ====================

  // Column configuration - accountNumber first per Brandi's requirements
  columns = [
    { key: "accountNumber", label: "Account", width: 100 },
    { key: "accountName", label: "Account Name", width: 180 },
    { key: "description", label: "Description", width: 250 },
    {
      key: "debitAmount",
      label: "Debit",
      width: 110,
      align: "right" as const,
      format: "currency",
      isSummable: true,
    },
    {
      key: "creditAmount",
      label: "Credit",
      width: 110,
      align: "right" as const,
      format: "currency",
      isSummable: true,
    },
    {
      key: "transactionDate",
      label: "Date",
      width: 100,
      format: "date",
    },
    { key: "plantId", label: "Plant", width: 70, align: "center" as const },
    { key: "reference", label: "Reference", width: 120 },
    { key: "jobId", label: "Job", width: 100 },
    {
      key: "runningBalance",
      label: "Balance",
      width: 120,
      align: "right" as const,
      format: "currency",
    },
    { key: "sourceType", label: "Source", width: 100 },
    { key: "createdBy", label: "Posted By", width: 120 },
  ];

  // ==================== Vendor/Customer Filtering (Phase 5) ====================

  selectedVendorIds: string[] = [];
  selectedCustomerIds: string[] = [];

  // ==================== Saved Configurations (Phase 7) ====================

  savedConfigurations: GLReportConfig[] = [];
  activeConfigurationId: string | null = null;

  constructor(rootStore: RootStore) {
    this.rootStore = rootStore;
    makeAutoObservable(this, { rootStore: false });
  }

  // ==================== Computed: Data Access ====================

  get glTransactionsStore() {
    return this.rootStore.serviceStores?.["gl-transactions"];
  }

  get accountBalancesStore() {
    return this.rootStore.serviceStores?.["account-balances"];
  }

  get fiscalPeriodsStore() {
    return this.rootStore.serviceStores?.["fiscal-periods"];
  }

  get glAccountsStore() {
    return this.rootStore.serviceStores?.["gl-accounts"];
  }

  get vendorsStore() {
    return this.rootStore.serviceStores?.["vendors"];
  }

  get customersStore() {
    return this.rootStore.serviceStores?.["customers"];
  }

  get vendors() {
    const storeEntities = this.vendorsStore?.entities as Array<{ id: string; name: string; isActive: boolean }>;
    return storeEntities || [];
  }

  get customers() {
    const storeEntities = this.customersStore?.entities as Array<{ id: string; name: string; isActive: boolean }>;
    return storeEntities || [];
  }

  get transactions(): GLTransaction[] {
    return (this.glTransactionsStore?.entities as GLTransaction[]) || [];
  }

  get accounts(): EnhancedGLAccount[] {
    return (this.glAccountsStore?.entities as EnhancedGLAccount[]) || [];
  }

  get periods(): FiscalPeriod[] {
    return (this.fiscalPeriodsStore?.entities as FiscalPeriod[]) || [];
  }

  get currentPeriod(): FiscalPeriod | null {
    const today = new Date().toISOString().split("T")[0];
    return (
      this.periods.find((p) => p.startDate <= today && p.endDate >= today) ||
      null
    );
  }

  // ==================== Computed: Date Range ====================

  get effectiveDateRange(): { start: string; end: string } {
    switch (this.dateRangeType) {
      case "this_period":
        return {
          start: this.currentPeriod?.startDate || "",
          end: this.currentPeriod?.endDate || "",
        };
      case "last_period": {
        const lastPeriod = this.periods.find(
          (p) => p.periodNumber === (this.currentPeriod?.periodNumber || 1) - 1
        );
        return {
          start: lastPeriod?.startDate || "",
          end: lastPeriod?.endDate || "",
        };
      }
      case "this_quarter":
        return this.getQuarterDates(0);
      case "last_quarter":
        return this.getQuarterDates(-1);
      case "this_year":
        return this.getYearDates(0);
      case "last_year":
        return this.getYearDates(-1);
      case "ytd": {
        const yearStart = `${new Date().getFullYear()}-01-01`;
        return { start: yearStart, end: new Date().toISOString().split("T")[0] };
      }
      case "custom":
        return {
          start: this.customStartDate || "",
          end: this.customEndDate || "",
        };
      default:
        return { start: "", end: "" };
    }
  }

  // ==================== Computed: Filtered Data ====================

  get filteredTransactions(): GLTransaction[] {
    let result = this.transactions;

    // Date range filter
    const { start, end } = this.effectiveDateRange;
    if (start && end) {
      result = result.filter(
        (t) => t.transactionDate >= start && t.transactionDate <= end
      );
    }

    // Plant filter (multi-select)
    if (this.selectedPlantIds.length > 0) {
      result = result.filter(
        (t) => !t.plantId || this.selectedPlantIds.includes(t.plantId)
      );
    }

    // Account filter
    if (this.selectedAccountIds.length > 0) {
      result = result.filter((t) =>
        this.selectedAccountIds.includes(t.accountId)
      );
    }

    // Account type filter
    if (this.selectedAccountTypes.length > 0) {
      result = result.filter((t) => {
        const account = this.accounts.find((a) => a.id === t.accountId);
        return (
          account && this.selectedAccountTypes.includes(account.accountType)
        );
      });
    }

    // Job filter
    if (this.selectedJobId) {
      result = result.filter((t) => t.jobId === this.selectedJobId);
    }

    // Amount filter
    if (this.amountFilterType === "debit") {
      result = result.filter((t) => t.debitAmount > 0);
    } else if (this.amountFilterType === "credit") {
      result = result.filter((t) => t.creditAmount > 0);
    }

    // Materiality threshold
    if (this.materialityThreshold) {
      result = result.filter(
        (t) =>
          t.debitAmount >= this.materialityThreshold! ||
          t.creditAmount >= this.materialityThreshold!
      );
    }

    // Vendor filter
    if (this.selectedVendorIds.length > 0) {
      result = result.filter(
        (t) => t.vendorId && this.selectedVendorIds.includes(t.vendorId)
      );
    }

    // Customer filter
    if (this.selectedCustomerIds.length > 0) {
      result = result.filter(
        (t) => t.customerId && this.selectedCustomerIds.includes(t.customerId)
      );
    }

    // Search
    if (this.searchQuery) {
      const query = this.searchQuery.toLowerCase();
      result = result.filter(
        (t) =>
          (this.searchFields.includes("description") &&
            t.description?.toLowerCase().includes(query)) ||
          (this.searchFields.includes("reference") &&
            t.reference?.toLowerCase().includes(query)) ||
          (this.searchFields.includes("account") &&
            (t.accountNumber?.toLowerCase().includes(query) ||
              t.accountName?.toLowerCase().includes(query)))
      );
    }

    return result;
  }

  // Sorted transactions
  get sortedTransactions(): GLTransaction[] {
    return [...this.filteredTransactions].sort((a, b) => {
      const aVal = a[this.sortColumn as keyof GLTransaction];
      const bVal = b[this.sortColumn as keyof GLTransaction];

      if (aVal === bVal) return 0;
      if (aVal === null || aVal === undefined) return 1;
      if (bVal === null || bVal === undefined) return -1;

      const comparison = aVal < bVal ? -1 : 1;
      return this.sortDirection === "asc" ? comparison : -comparison;
    });
  }

  // Paginated transactions
  get paginatedTransactions(): GLTransaction[] {
    const start = (this.currentPage - 1) * this.pageSize;
    return this.sortedTransactions.slice(start, start + this.pageSize);
  }

  // Add running balance to transactions
  get transactionsWithBalance(): (GLTransaction & { runningBalance: number })[] {
    let balance = 0;
    return this.sortedTransactions.map((t) => {
      balance += t.debitAmount - t.creditAmount;
      return { ...t, runningBalance: balance };
    });
  }

  // ==================== Computed: Summary Stats ====================

  get transactionSummary() {
    const filtered = this.filteredTransactions;
    return {
      totalTransactions: filtered.length,
      totalDebits: filtered.reduce((sum, t) => sum + t.debitAmount, 0),
      totalCredits: filtered.reduce((sum, t) => sum + t.creditAmount, 0),
      netChange: filtered.reduce(
        (sum, t) => sum + t.debitAmount - t.creditAmount,
        0
      ),
      uniqueAccounts: new Set(filtered.map((t) => t.accountId)).size,
      dateRange: this.effectiveDateRange,
    };
  }

  get plantBreakdown(): Map<string, { debits: number; credits: number }> {
    const breakdown = new Map<string, { debits: number; credits: number }>();

    for (const t of this.filteredTransactions) {
      const plantId = t.plantId || "unassigned";
      const current = breakdown.get(plantId) || { debits: 0, credits: 0 };
      breakdown.set(plantId, {
        debits: current.debits + t.debitAmount,
        credits: current.credits + t.creditAmount,
      });
    }

    return breakdown;
  }

  get totalPages(): number {
    return Math.ceil(this.filteredTransactions.length / this.pageSize);
  }

  // ==================== Computed: Column Configuration (Phase 2) ====================

  /**
   * Returns columns filtered by visibility and sorted by columnOrder
   */
  get orderedVisibleColumns() {
    return this.columnOrder
      .filter((key) => this.visibleColumns.includes(key))
      .map((key) => this.columns.find((c) => c.key === key))
      .filter(Boolean);
  }

  /**
   * Returns all columns sorted by columnOrder (for column config dialog)
   */
  get orderedColumns() {
    return this.columnOrder
      .map((key) => this.columns.find((c) => c.key === key))
      .filter(Boolean);
  }

  // ==================== Computed: Trend Indicators (Phase 8) ====================

  /**
   * Calculate previous period date range for comparison
   */
  get previousPeriodDateRange(): { start: string; end: string } {
    const { start, end } = this.effectiveDateRange;
    if (!start || !end) return { start: "", end: "" };

    const startDate = new Date(start);
    const endDate = new Date(end);
    const periodLength = endDate.getTime() - startDate.getTime();

    const prevEnd = new Date(startDate.getTime() - 1); // Day before current period
    const prevStart = new Date(prevEnd.getTime() - periodLength);

    return {
      start: prevStart.toISOString().split("T")[0],
      end: prevEnd.toISOString().split("T")[0],
    };
  }

  /**
   * Summary for previous period (for trend comparison)
   */
  get previousPeriodSummary() {
    const { start, end } = this.previousPeriodDateRange;
    if (!start || !end) {
      return { totalDebits: 0, totalCredits: 0, netChange: 0 };
    }

    const prevTransactions = this.transactions.filter(
      (t) => t.transactionDate >= start && t.transactionDate <= end
    );

    return {
      totalDebits: prevTransactions.reduce((sum, t) => sum + t.debitAmount, 0),
      totalCredits: prevTransactions.reduce((sum, t) => sum + t.creditAmount, 0),
      netChange: prevTransactions.reduce(
        (sum, t) => sum + t.debitAmount - t.creditAmount,
        0
      ),
    };
  }

  /**
   * Trend indicators comparing current vs previous period
   */
  get trendIndicators() {
    const current = this.transactionSummary;
    const previous = this.previousPeriodSummary;

    const calculateTrend = (currentVal: number, previousVal: number) => {
      if (previousVal === 0) {
        return { direction: "neutral" as const, percent: 0 };
      }
      const percent = ((currentVal - previousVal) / Math.abs(previousVal)) * 100;
      return {
        direction: percent > 0 ? ("up" as const) : percent < 0 ? ("down" as const) : ("neutral" as const),
        percent: Math.abs(percent),
      };
    };

    return {
      debits: calculateTrend(current.totalDebits, previous.totalDebits),
      credits: calculateTrend(current.totalCredits, previous.totalCredits),
      netChange: calculateTrend(current.netChange, previous.netChange),
    };
  }

  // ==================== Actions: Filtering ====================

  setDateRangeType = (type: DateRangeType) => {
    this.dateRangeType = type;
    if (type !== "custom") {
      this.customStartDate = null;
      this.customEndDate = null;
    }
  };

  setCustomDateRange = (start: string, end: string) => {
    this.dateRangeType = "custom";
    this.customStartDate = start;
    this.customEndDate = end;
  };

  setSelectedPlants = (plantIds: string[]) => {
    this.selectedPlantIds = plantIds;
  };

  togglePlant = (plantId: string) => {
    if (this.selectedPlantIds.includes(plantId)) {
      this.selectedPlantIds = this.selectedPlantIds.filter((p) => p !== plantId);
    } else {
      this.selectedPlantIds = [...this.selectedPlantIds, plantId];
    }
  };

  setSelectedAccount = (accountId: string | null) => {
    this.selectedAccountId = accountId;
    this.selectedAccountIds = accountId ? [accountId] : [];
    this.viewMode = accountId ? "transactions" : "summary";
  };

  setMaterialityThreshold = (amount: number | null) => {
    this.materialityThreshold = amount;
  };

  setSearchQuery = (query: string) => {
    this.searchQuery = query;
    this.currentPage = 1;
  };

  clearAllFilters = () => {
    this.dateRangeType = "this_period";
    this.customStartDate = null;
    this.customEndDate = null;
    this.selectedPlantIds = [];
    this.selectedAccountIds = [];
    this.selectedAccountTypes = [];
    this.selectedJobId = null;
    this.amountFilterType = "any";
    this.materialityThreshold = null;
    this.searchQuery = "";
    this.selectedVendorIds = [];
    this.selectedCustomerIds = [];
    this.currentPage = 1;
  };

  // ==================== Actions: Vendor/Customer Filtering (Phase 5) ====================

  setSelectedVendors = (vendorIds: string[]) => {
    this.selectedVendorIds = vendorIds;
    this.currentPage = 1;
  };

  setSelectedCustomers = (customerIds: string[]) => {
    this.selectedCustomerIds = customerIds;
    this.currentPage = 1;
  };

  toggleVendor = (vendorId: string) => {
    if (this.selectedVendorIds.includes(vendorId)) {
      this.selectedVendorIds = this.selectedVendorIds.filter((v) => v !== vendorId);
    } else {
      this.selectedVendorIds = [...this.selectedVendorIds, vendorId];
    }
    this.currentPage = 1;
  };

  toggleCustomer = (customerId: string) => {
    if (this.selectedCustomerIds.includes(customerId)) {
      this.selectedCustomerIds = this.selectedCustomerIds.filter((c) => c !== customerId);
    } else {
      this.selectedCustomerIds = [...this.selectedCustomerIds, customerId];
    }
    this.currentPage = 1;
  };

  // ==================== Actions: Account Multi-Select (Phase 3) ====================

  setSelectedAccounts = (accountIds: string[]) => {
    this.selectedAccountIds = accountIds;
    this.currentPage = 1;
  };

  toggleAccount = (accountId: string) => {
    if (this.selectedAccountIds.includes(accountId)) {
      this.selectedAccountIds = this.selectedAccountIds.filter((a) => a !== accountId);
    } else {
      this.selectedAccountIds = [...this.selectedAccountIds, accountId];
    }
    this.currentPage = 1;
  };

  // ==================== Actions: Display ====================

  setTableDensity = (density: "condensed" | "regular" | "relaxed") => {
    this.tableDensity = density;
  };

  setPageSize = (size: number) => {
    this.pageSize = size;
    this.currentPage = 1;
  };

  setPage = (page: number) => {
    this.currentPage = Math.max(1, Math.min(page, this.totalPages));
  };

  setSortColumn = (column: string) => {
    if (this.sortColumn === column) {
      this.sortDirection = this.sortDirection === "asc" ? "desc" : "asc";
    } else {
      this.sortColumn = column;
      this.sortDirection = "desc";
    }
  };

  toggleColumnVisibility = (column: string) => {
    if (this.visibleColumns.includes(column)) {
      this.visibleColumns = this.visibleColumns.filter((c) => c !== column);
    } else {
      this.visibleColumns = [...this.visibleColumns, column];
    }
  };

  // ==================== Actions: Column Configuration (Phase 2) ====================

  setVisibleColumns = (columns: string[]) => {
    this.visibleColumns = columns;
  };

  setColumnOrder = (order: string[]) => {
    this.columnOrder = order;
  };

  resetColumnsToDefault = () => {
    this.visibleColumns = [
      "accountNumber",
      "accountName",
      "description",
      "debitAmount",
      "creditAmount",
    ];
    this.columnOrder = [
      "accountNumber",
      "accountName",
      "description",
      "debitAmount",
      "creditAmount",
      "transactionDate",
      "plantId",
      "reference",
      "jobId",
      "runningBalance",
      "sourceType",
      "createdBy",
    ];
  };

  moveColumn = (fromIndex: number, toIndex: number) => {
    const newOrder = [...this.columnOrder];
    const [removed] = newOrder.splice(fromIndex, 1);
    newOrder.splice(toIndex, 0, removed);
    this.columnOrder = newOrder;
  };

  // ==================== Actions: Selection ====================

  toggleTransactionSelection = (transactionId: string) => {
    if (this.selectedTransactionIds.has(transactionId)) {
      this.selectedTransactionIds.delete(transactionId);
    } else {
      this.selectedTransactionIds.add(transactionId);
    }
  };

  selectAllVisible = () => {
    for (const t of this.paginatedTransactions) {
      this.selectedTransactionIds.add(t.transactionId);
    }
  };

  clearSelection = () => {
    this.selectedTransactionIds.clear();
  };

  // ==================== Actions: Drill-down ====================

  drillToTransaction = (transactionId: string) => {
    this.selectedTransactionId = transactionId;
    this.viewMode = "detail";
    this.rootStore.routerStore?.openDialog({
      dialogName: "TransactionDetailDialog",
      dialogContext: transactionId,
    });
  };

  drillToAccount = (accountId: string) => {
    this.setSelectedAccount(accountId);
    this.viewMode = "transactions";
  };

  drillToSource = (sourceType: TransactionSourceType, sourceId: string) => {
    const routes: Record<TransactionSourceType, string> = {
      journal_entry: "JournalEntryDetails",
      invoice: "InvoiceDetails",
      bill: "BillDetails",
      payment: "PaymentDetails",
      payroll: "PayrollDetails",
      inventory: "InventoryDetails",
      wip_adjustment: "WIPDetails",
      depreciation: "DepreciationDetails",
      recurring: "RecurringDetails",
    };

    const route = routes[sourceType];
    if (route) {
      this.rootStore.routerStore?.openDialog({
        dialogName: route,
        dialogContext: sourceId,
      });
    }
  };

  // ==================== Actions: Data Loading ====================

  loadData = async () => {
    this.isLoading = true;
    try {
      const { start, end } = this.effectiveDateRange;

      await Promise.all([
        this.glTransactionsStore?.find({
          query: {
            transactionDate: { $gte: start, $lte: end },
            $limit: 10000,
          },
        }),
        this.glAccountsStore?.find({ query: { $limit: 1000 } }),
        this.fiscalPeriodsStore?.find({ query: { $limit: 100 } }),
      ]);
    } finally {
      runInAction(() => {
        this.isLoading = false;
      });
    }
  };

  // ==================== Actions: Export ====================

  private getTransactionsByPlant(): Map<string, GLTransaction[]> {
    const byPlant = new Map<string, GLTransaction[]>();
    for (const t of this.sortedTransactions) {
      const plantId = t.plantId || "unassigned";
      if (!byPlant.has(plantId)) {
        byPlant.set(plantId, []);
      }
      byPlant.get(plantId)!.push(t);
    }
    return byPlant;
  }

  private getPlantName(plantId: string): string {
    if (plantId === "unassigned") return "Unassigned";
    const plants = PLANTS_BY_COMPANY["H2"] || [];
    const plant = plants.find((p) => p.id === plantId);
    return plant ? `${plantId} - ${plant.name}` : plantId;
  }

  private getExportColumns(): ColumnConfig[] {
    return [
      { key: "transactionDate", label: "Date", format: "date" },
      { key: "plantId", label: "Plant", format: "text" },
      { key: "accountNumber", label: "Account #", format: "text" },
      { key: "accountName", label: "Account Name", format: "text", width: 25 },
      { key: "description", label: "Description", format: "text", width: 30 },
      { key: "reference", label: "Reference", format: "text" },
      { key: "jobId", label: "Job", format: "text" },
      { key: "debitAmount", label: "Debit", format: "currency" },
      { key: "creditAmount", label: "Credit", format: "currency" },
      { key: "sourceType", label: "Source", format: "text" },
    ];
  }

  private getExportFileName(): string {
    const { start, end } = this.effectiveDateRange;
    return `general-ledger_${start}_${end}`;
  }

  exportToExcel = async (options?: ExportMethodOptions) => {
    this.isExporting = true;
    try {
      const { includeSummary = true, splitByPlant = false } = options || {};

      if (splitByPlant) {
        // Export multiple sheets, one per plant
        const byPlant = this.getTransactionsByPlant();
        const sheets: MultiSheetData<Record<string, unknown>>[] = [];

        for (const [plantId, transactions] of byPlant) {
          sheets.push({
            sheetName: this.getPlantName(plantId),
            data: transactions as unknown as Record<string, unknown>[],
          });
        }

        await exportToExcelMultiSheet(sheets, this.getExportColumns(), {
          fileName: this.getExportFileName(),
          title: "General Ledger Report",
          includeHeaders: true,
          includeSummary,
        });
      } else {
        // Single sheet export
        await exportToExcel(
          this.sortedTransactions as unknown as Record<string, unknown>[],
          this.getExportColumns(),
          {
            fileName: this.getExportFileName(),
            sheetName: "General Ledger",
            title: "General Ledger Report",
            includeHeaders: true,
            includeSummary,
          }
        );
      }
    } finally {
      runInAction(() => {
        this.isExporting = false;
      });
    }
  };

  exportToPDF = async (options?: ExportMethodOptions) => {
    this.isExporting = true;
    try {
      const { includeSummary = true, splitByPlant = false } = options || {};

      if (splitByPlant) {
        // Export single PDF with page breaks between plants
        const byPlant = this.getTransactionsByPlant();
        const sections: PdfSectionData<Record<string, unknown>>[] = [];

        for (const [plantId, transactions] of byPlant) {
          sections.push({
            sectionTitle: this.getPlantName(plantId),
            data: transactions as unknown as Record<string, unknown>[],
          });
        }

        exportToPdfMultiSection(sections, this.getExportColumns(), {
          fileName: this.getExportFileName(),
          title: "General Ledger Report",
          includeHeaders: true,
          includeSummary,
        });
      } else {
        exportToPdf(
          this.sortedTransactions as unknown as Record<string, unknown>[],
          this.getExportColumns(),
          {
            fileName: this.getExportFileName(),
            title: "General Ledger Report",
            includeHeaders: true,
            includeSummary,
          }
        );
      }
    } finally {
      runInAction(() => {
        this.isExporting = false;
      });
    }
  };

  exportToCSV = async (options?: ExportMethodOptions) => {
    this.isExporting = true;
    try {
      const { includeHeaders = true, splitByPlant = false } = options || {};

      if (splitByPlant) {
        // Export separate CSVs for each plant
        const byPlant = this.getTransactionsByPlant();

        for (const [plantId, transactions] of byPlant) {
          exportToCsv(
            transactions as unknown as Record<string, unknown>[],
            this.getExportColumns(),
            {
              fileName: `${this.getExportFileName()}_plant-${plantId}`,
              includeHeaders,
            }
          );
          // Small delay between downloads
          await new Promise((r) => setTimeout(r, 200));
        }
      } else {
        exportToCsv(
          this.sortedTransactions as unknown as Record<string, unknown>[],
          this.getExportColumns(),
          {
            fileName: this.getExportFileName(),
            includeHeaders,
          }
        );
      }
    } finally {
      runInAction(() => {
        this.isExporting = false;
      });
    }
  };

  // ==================== Actions: Saved Configurations (Phase 7) ====================

  /**
   * Save current view configuration
   */
  saveConfiguration = (name: string, description?: string, isShared = false) => {
    const authStore = (this.rootStore as any).authStore;
    const currentUser = authStore?.currentUser;

    const config: GLReportConfig = {
      id: `config-${Date.now()}`,
      name,
      description,
      createdBy: currentUser?.userId || currentUser?.id,
      createdAt: new Date().toISOString(),
      isShared,
      filters: {
        dateRangeType: this.dateRangeType,
        customStartDate: this.customStartDate || undefined,
        customEndDate: this.customEndDate || undefined,
        plantIds: [...this.selectedPlantIds],
        accountIds: [...this.selectedAccountIds],
        accountTypes: [...this.selectedAccountTypes],
        vendorIds: [...this.selectedVendorIds],
        customerIds: [...this.selectedCustomerIds],
      },
      display: {
        visibleColumns: [...this.visibleColumns],
        columnOrder: [...this.columnOrder],
        sortColumn: this.sortColumn,
        sortDirection: this.sortDirection,
        tableDensity: this.tableDensity,
      },
    };

    this.savedConfigurations = [...this.savedConfigurations, config];
    this.activeConfigurationId = config.id;

    // Persist to localStorage
    this.persistConfigurations();

    return config;
  };

  /**
   * Load a saved configuration
   */
  loadConfiguration = (configId: string) => {
    const config = this.savedConfigurations.find((c) => c.id === configId);
    if (!config) return;

    // Apply filters
    this.dateRangeType = config.filters.dateRangeType;
    this.customStartDate = config.filters.customStartDate || null;
    this.customEndDate = config.filters.customEndDate || null;
    this.selectedPlantIds = [...config.filters.plantIds];
    this.selectedAccountIds = [...config.filters.accountIds];
    this.selectedAccountTypes = [...config.filters.accountTypes];
    this.selectedVendorIds = config.filters.vendorIds ? [...config.filters.vendorIds] : [];
    this.selectedCustomerIds = config.filters.customerIds ? [...config.filters.customerIds] : [];

    // Apply display settings
    this.visibleColumns = [...config.display.visibleColumns];
    this.columnOrder = [...config.display.columnOrder];
    this.sortColumn = config.display.sortColumn;
    this.sortDirection = config.display.sortDirection;
    this.tableDensity = config.display.tableDensity;

    this.activeConfigurationId = configId;
    this.currentPage = 1;
  };

  /**
   * Delete a saved configuration
   */
  deleteConfiguration = (configId: string) => {
    this.savedConfigurations = this.savedConfigurations.filter((c) => c.id !== configId);
    if (this.activeConfigurationId === configId) {
      this.activeConfigurationId = null;
    }
    this.persistConfigurations();
  };

  /**
   * Update an existing configuration
   */
  updateConfiguration = (configId: string, updates: Partial<Pick<GLReportConfig, "name" | "description" | "isShared">>) => {
    this.savedConfigurations = this.savedConfigurations.map((c) =>
      c.id === configId ? { ...c, ...updates } : c
    );
    this.persistConfigurations();
  };

  /**
   * Persist configurations to localStorage
   */
  private persistConfigurations = () => {
    try {
      localStorage.setItem("gl-saved-configurations", JSON.stringify(this.savedConfigurations));
    } catch (e) {
      // Error persisting GL configurations
    }
  };

  /**
   * Load configurations from localStorage
   */
  loadPersistedConfigurations = () => {
    try {
      const stored = localStorage.getItem("gl-saved-configurations");
      if (stored) {
        this.savedConfigurations = JSON.parse(stored);
      }
    } catch (e) {
      // Error loading GL configurations
    }
  };

  /**
   * Get personal configurations (created by current user)
   */
  get personalConfigurations(): GLReportConfig[] {
    return this.savedConfigurations.filter((c) => !c.isShared);
  }

  /**
   * Get shared configurations
   */
  get sharedConfigurations(): GLReportConfig[] {
    return this.savedConfigurations.filter((c) => c.isShared);
  }

  // ==================== Helper Methods ====================

  private getQuarterDates(offset: number): { start: string; end: string } {
    const now = new Date();
    const quarter = Math.floor(now.getMonth() / 3) + offset;
    const year = now.getFullYear() + Math.floor(quarter / 4);
    const q = ((quarter % 4) + 4) % 4;

    const startMonth = q * 3;
    const start = new Date(year, startMonth, 1);
    const end = new Date(year, startMonth + 3, 0);

    return {
      start: start.toISOString().split("T")[0],
      end: end.toISOString().split("T")[0],
    };
  }

  private getYearDates(offset: number): { start: string; end: string } {
    const year = new Date().getFullYear() + offset;
    return {
      start: `${year}-01-01`,
      end: `${year}-12-31`,
    };
  }
}

export default GeneralLedgerStore;
